import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import AdminTopBar from './AdminTopBar';
import AdminBreadcrumb, { type AdminBreadcrumbSegment } from './AdminBreadcrumb';
import Sidebar from './Sidebar';
import { PortalRefreshProvider } from '../../contexts/PortalRefreshContext';
import { NotificationUnreadProvider } from '../../contexts/NotificationUnreadContext';
import type { AdminNavigation, AdminUserSummary } from '../../types/admin';

interface AdminLayoutProps {
  navigation: AdminNavigation;
  user: AdminUserSummary;
  onLogout: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  breadcrumb?: AdminBreadcrumbSegment[];
  brandAlt?: string;
  helpPath?: string;
  children: ReactNode;
}

function unlockPageInteractions() {
  document.body.style.overflow = '';
  document.body.style.pointerEvents = '';
  document.documentElement.style.overflow = '';
  document.documentElement.style.pointerEvents = '';
}

function dismissBlockingDevOverlays() {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  document.getElementById('webpack-dev-server-client-overlay')?.remove();
  document.querySelectorAll('iframe[id*="webpack"], iframe[src*="webpack"]').forEach((node) => {
    node.remove();
  });
}

export default function AdminLayout({
  navigation,
  user,
  onLogout,
  searchQuery,
  onSearchChange,
  breadcrumb,
  brandAlt,
  helpPath,
  children,
}: AdminLayoutProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isDashboard =
    location.pathname === '/admin' ||
    location.pathname === '/admin/dashboard' ||
    location.pathname.endsWith('/admin/dashboard');

  useEffect(() => {
    document.body.classList.add('admin-route');
    unlockPageInteractions();
    dismissBlockingDevOverlays();
    return () => {
      document.body.classList.remove('admin-route');
      unlockPageInteractions();
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    unlockPageInteractions();
    dismissBlockingDevOverlays();
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      unlockPageInteractions();
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        unlockPageInteractions();
        dismissBlockingDevOverlays();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    unlockPageInteractions();
  };

  return (
    <PortalRefreshProvider>
    <NotificationUnreadProvider>
    <div className="admin-shell overflow-hidden bg-surface font-body text-on-surface antialiased">
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 overflow-hidden lg:flex">
        <Sidebar items={navigation} onCloseMobile={() => undefined} brandAlt={brandAlt} />
      </aside>

      <div className="flex h-screen flex-col overflow-hidden lg:ml-64">
        <AdminTopBar
          user={user}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          onMenuToggle={() => setMobileMenuOpen(true)}
          onLogout={onLogout}
          helpPath={helpPath}
        />

        <main
          className={[
            'flex-1 overflow-y-auto bg-admin-canvas',
            isDashboard ? 'p-6 pt-4 lg:p-10' : 'p-6 lg:p-8',
          ].join(' ')}
        >
          {breadcrumb && breadcrumb.length > 0 ? (
            <div className="mb-3 rounded-xl border border-outline-variant/15 bg-white px-4 py-3 shadow-sm">
              <AdminBreadcrumb segments={breadcrumb} />
            </div>
          ) : null}
          {children}
        </main>
      </div>

      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden" role="presentation">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Fermer le menu"
            onClick={closeMobileMenu}
          />
          <div className="absolute left-0 top-0 flex h-full w-64 max-w-[85vw] flex-col overflow-hidden shadow-2xl">
            <Sidebar items={navigation} onCloseMobile={closeMobileMenu} brandAlt={brandAlt} />
          </div>
        </div>
      ) : null}
    </div>
    </NotificationUnreadProvider>
    </PortalRefreshProvider>
  );
}
